// Расчеты по тренировкам из раздела «Мои тренировки».
// Участки приходят с бэкенда (WorkoutFileParser): мощность — доля FTP,
// рампа идет от power_low к power_high, у free/max мощности нет.

// Семь зон Коггана — та же модель, что у ROUVY. Верхняя граница включительно:
// 55 % — еще Z1, 56 % — уже Z2. Цвета — привычная велосипедистам шкала,
// подобраны так, чтобы соседние зоны различались и при дальтонизме;
// в обеих темах одинаковые, поэтому зону всегда подписываем Z1…Z7
export const ZONES = [
  { id: 1, max: 0.55, key: 'recovery', color: 'var(--c-94a3b8-bg)' },
  { id: 2, max: 0.75, key: 'endurance', color: 'var(--c-3b82f6-bg)' },
  { id: 3, max: 0.9, key: 'tempo', color: 'var(--c-16a34a-bg)' },
  { id: 4, max: 1.05, key: 'threshold', color: 'var(--c-e9be0c-bg)' },
  { id: 5, max: 1.2, key: 'vo2max', color: 'var(--c-f26b0f-bg)' },
  { id: 6, max: 1.5, key: 'anaerobic', color: 'var(--c-b81c1c-bg)' },
  { id: 7, max: Infinity, key: 'neuromuscular', color: 'var(--c-9333ea-bg)' },
];

const EPS = 1e-9;

export const zoneFor = (power) => ZONES.find((zone) => power <= zone.max + EPS) || ZONES[ZONES.length - 1];

// Нижняя граница зоны в процентах: у Z2 это 56, а не 55
export const zoneRange = (zone) => {
  const index = ZONES.indexOf(zone);
  const low = index === 0 ? 0 : Math.round(ZONES[index - 1].max * 100) + 1;
  return zone.max === Infinity ? `>${low - 1}%` : `${low}–${Math.round(zone.max * 100)}%`;
};

export const hasPower = (segment) => segment.kind === 'steady' || segment.kind === 'ramp';

// Делит участок на куски по границам зон: рампа 50→80 % дает куски Z1, Z2, Z3,
// каждый со своим временем. Это и раскраска графика, и время в зонах
export const zonePieces = (segment) => {
  const { start, duration, power_low: p0, power_high: p1 } = segment;
  if (!hasPower(segment)) return [];
  if (segment.kind === 'steady' || p0 === p1) {
    return [{ t0: start, t1: start + duration, p0, p1: p0, zone: zoneFor(p0) }];
  }

  const lo = Math.min(p0, p1);
  const hi = Math.max(p0, p1);
  const cuts = ZONES.map((zone) => zone.max).filter((max) => max > lo + EPS && max < hi - EPS);
  // Порядок границ — по ходу рампы
  const levels = [p0, ...(p1 > p0 ? cuts : cuts.reverse()), p1];

  return levels.slice(0, -1).map((from, i) => {
    const to = levels[i + 1];
    const t0 = start + ((from - p0) / (p1 - p0)) * duration;
    const t1 = start + ((to - p0) / (p1 - p0)) * duration;
    return { t0, t1, p0: from, p1: to, zone: zoneFor((from + to) / 2) };
  });
};

export const zoneDistribution = (segments) => {
  const seconds = Object.fromEntries(ZONES.map((zone) => [zone.id, 0]));
  let free = 0;

  segments.forEach((segment) => {
    if (!hasPower(segment)) {
      free += segment.duration;
      return;
    }
    zonePieces(segment).forEach((piece) => {
      seconds[piece.zone.id] += piece.t1 - piece.t0;
    });
  });

  return { zones: ZONES.map((zone) => ({ zone, seconds: seconds[zone.id] })), free };
};

// Посекундная мощность: середина каждой секунды рампы; без мощности — null
const secondSamples = (segments) => {
  const samples = [];
  segments.forEach((segment) => {
    for (let s = 0; s < segment.duration; s += 1) {
      if (!hasPower(segment)) {
        samples.push(null);
      } else {
        const k = (s + 0.5) / segment.duration;
        samples.push(segment.power_low + (segment.power_high - segment.power_low) * k);
      }
    }
  });
  return samples;
};

// Средняя, нормализованная мощность (скользящее 30 с, четвертая степень), IF и TSS.
// Все в долях FTP; участки без мощности (свободная езда, спринт) не учитываются
export const workoutMetrics = (segments) => {
  const samples = secondSamples(segments).filter((value) => value !== null);
  const n = samples.length;
  if (n === 0) return { counted: 0, avg: null, np: null, intensity: null, tss: null };

  const avg = samples.reduce((sum, value) => sum + value, 0) / n;

  let np = avg;
  if (n >= 30) {
    let windowSum = samples.slice(0, 30).reduce((sum, value) => sum + value, 0);
    let fourth = (windowSum / 30) ** 4;
    for (let i = 30; i < n; i += 1) {
      windowSum += samples[i] - samples[i - 30];
      fourth += (windowSum / 30) ** 4;
    }
    np = (fourth / (n - 29)) ** 0.25;
  }

  return { counted: n, avg, np, intensity: np, tss: (n / 3600) * np * np * 100 };
};

const sameSegment = (a, b) =>
  a.kind === b.kind && a.duration === b.duration && a.power_low === b.power_low &&
  a.power_high === b.power_high && a.cadence === b.cadence;

// Сколько раз блок из size участков, начиная с from, повторяется подряд
const repeatCount = (segments, from, size) => {
  let count = 1;
  const matches = (copy) => {
    for (let k = 0; k < size; k += 1) {
      if (!sameSegment(segments[from + k], segments[from + copy * size + k])) return false;
    }
    return true;
  };
  while (from + (count + 1) * size <= segments.length && matches(count)) count += 1;
  return count;
};

// Сворачивает повторы в «N × (…)»: ERG/MRC повторов не знают, поэтому ищем их
// по самим участкам — блок из 1–4 одинаковых участков подряд хотя бы дважды
export const groupSegments = (segments) => {
  const rows = [];
  let i = 0;

  while (i < segments.length) {
    let best = null;
    for (let size = 1; size <= 4; size += 1) {
      const count = repeatCount(segments, i, size);
      if (count >= 2 && (!best || count * size > best.count * best.size)) best = { size, count };
    }

    if (best) {
      const from = i;
      const items = segments.slice(from, from + best.size).map((segment, k) => ({ segment, index: from + k }));
      const last = segments[i + best.count * best.size - 1];
      rows.push({ type: 'repeat', count: best.count, items, start: segments[i].start, end: last.start + last.duration });
      i += best.count * best.size;
    } else {
      rows.push({ type: 'single', segment: segments[i], index: i });
      i += 1;
    }
  }

  return rows;
};

// 1:05:00 / 12:30
export const formatDuration = (totalSeconds) => {
  const seconds = Math.round(totalSeconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (value) => String(value).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

export const formatPercent = (power) => `${Math.round(power * 100)}%`;

export const formatWatts = (power, ftp) => (ftp ? `${Math.round(power * ftp)}` : null);

// План тренировки — ERG/MRC/ZWO, выполненный заезд — GPX/FIT/TCX
export const PLAN_FORMATS = ['erg', 'mrc', 'zwo'];
export const ACTIVITY_FORMATS = ['gpx', 'fit', 'tcx'];

export const WORKOUT_FILE_ACCEPT = [...PLAN_FORMATS, ...ACTIVITY_FORMATS].map((ext) => `.${ext}`).join(',');

export const workoutFileFormat = (name) => {
  const match = /\.([a-z0-9]+)$/i.exec(name || '');
  const ext = match ? match[1].toLowerCase() : '';
  return [...PLAN_FORMATS, ...ACTIVITY_FORMATS].includes(ext) ? ext : null;
};
